import * as Sentry from "@sentry/browser";
import { initFieldsLogicRegistry } from "@smartsuite/fields-logic/lib/field-type-registry/field-logic-init";
import ReactDOM from "react-dom/client";
import ResizeObserverPolyfill from "resize-observer-polyfill";
import { App } from "./App";
import reportWebVitals from "./reportWebVitals";
import { GlobalErrorBoundary } from "./components/GlobalErrorBoundary/GlobalErrorBoundary";

import { MockAppRecordsService } from "./registry/fields-logic/fake-app-record-service";
import "./styles.sass";
import { FieldTypesConfig, Member } from "@smartsuite/types";
import { fieldTypes } from "./registry/fields-logic/field-types-map";
import { env } from "./utils/env";
import { hasOnlyForeignFrames } from "./utils/sentryFilters";

// Polyfill for Array.prototype.at() - ES2022 feature
// Required for @smartsuite/smartdoc library compatibility with older browsers
if (!Array.prototype.at) {
  // eslint-disable-next-line no-extend-native, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  Object.defineProperty(Array.prototype, "at", {
    value: function (index: number) {
      const len = Number(this.length);
      const numIndex = Number(index);
      const relativeIndex = numIndex >= 0 ? numIndex : len + numIndex;
      if (relativeIndex < 0 || relativeIndex >= len) {
        return undefined;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this[relativeIndex];
    },
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

// Polyfill for String.prototype.replaceAll() — ES2021, missing on Chrome < 85
// (still present in the wild on older budget Android devices). FORMS-WEB-TV.
if (!String.prototype.replaceAll) {
  // eslint-disable-next-line no-extend-native
  Object.defineProperty(String.prototype, "replaceAll", {
    value: function (
      this: string,
      search: string | RegExp,
      replacement: string | ((substring: string, ...args: unknown[]) => string)
    ) {
      if (search instanceof RegExp) {
        if (!search.global) {
          throw new TypeError("String.prototype.replaceAll called with a non-global RegExp");
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.replace(search, replacement as any);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return this.split(search).join(replacement as any);
    },
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

// iOS Lockdown Mode strips ResizeObserver (FORMS-WEB-W5); react-resize-detector
// reads window.ResizeObserver at mount time, so the polyfill must be assigned
// synchronously before the first render.
if (!window.ResizeObserver) {
  window.ResizeObserver = ResizeObserverPolyfill;
}

const isProduction = process.env.NODE_ENV === "production";

Sentry.init({
  enabled: !!env.REACT_APP_SENTRY_ENVIRONMENT,
  dsn: "https://8ae451a99c4448709c18eaf53b6dbea5@o1324044.ingest.sentry.io/6594505",
  // Session Replay is attached lazily after `load` (see below) so its code is
  // not part of the critical-path bundle. Profiling was dropped deliberately:
  // it added per-transaction runtime overhead on a public form.
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: isProduction ? 0.1 : 1.0,
  // Replay on errors buffers and is cheap until an error happens — keep it at
  // 100%; continuous session recording is the expensive part, sample it low.
  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: isProduction ? 0.01 : 1.0,
  environment: env.REACT_APP_SENTRY_ENVIRONMENT,
  release: `forms-web@${process.env.REACT_APP_COMMIT_SHA}`,
  ignoreErrors: [
    "Non-Error exception captured",
    "Non-Error promise rejection captured",
    "Object captured as promise rejection with keys: code, data, message",
    // Non-Error promise rejections with no stack: a synthetic CustomEvent (Safari)
    // and an empty reason object bubbled to onunhandledrejection (FORMS-WEB-DV, SA)
    /Event `?CustomEvent`?.*captured as promise rejection/i,
    /Object captured as promise rejection with keys: \[object has no keys\]/,
    "The operation is insecure.",
    "Failed to read the 'localStorage' property from 'Window'",
    /window\.webkit\.messageHandlers/i,
    "xbrowser is not defined",
    // Browser extensions
    /DarkReader/i,
    /window\.ethereum/i,
    // WebExtension storage API from an injected content script, not page code (FORMS-WEB-W8)
    /browser\.storage\.local/i,
    // In-app browser native bridges (Telegram, Android WebView, Shopify, etc.)
    /Error invoking \w+: Method not found/i,
    /Error invoking postMessage: Java object is gone/,
    /Error invoking enableDidUserTypeOnKeyboardLogging/,
    /IABShopifyRequestJSDebugMCBoolValue/,
    // Facebook in-app browser injected native bridge rejection (FORMS-WEB-WB)
    /OperationError: Non-recoverable error\. Do not retry\./,
    // Stale-deploy chunk loads: an old tab requests a hashed JS/CSS chunk that
    // no longer exists and receives index.html instead (FORMS-WEB-QY, SS, KW)
    /Loading (CSS )?chunk .+ failed/i,
    /ChunkLoadError/,
    /Importing a module script failed/i,
    /error loading dynamically imported module/i,
    /Unexpected token '<'/,
    // Transient browser fetch/XHR connectivity failures (network drop, navigation
    // away, timeout) surfaced as unhandled rejections with no stack (FORMS-WEB-K8, G3)
    /^TypeError: (network error|Load failed)$/i,
    /Failed to fetch/i,
    // Same transient failure raised from inside a blob: worker on iOS Safari, where it carries
    // the NetworkError name instead of TypeError (FORMS-WEB-QP)
    /^(?:Error: )?NetworkError: Load failed$/i,
    // pdf.js worker bundle failed to load from its CDN inside a blob worker (FORMS-WEB-RT)
    /Failed to execute 'importScripts' on 'WorkerGlobalScope'/,
    // Third-party in-app browser bridges and blocked loaders
    /Can't find variable: SCDynimacBridge/,
    /feature named `?pageContext`? was not found/,
    /google\.maps\.__ib__/,
    // Google Maps JS loader can't fetch a sublibrary (FORMS-WEB-N0)
    /Could not load "\w+"/,
    // Page embedded in a cross-origin iframe; host/third-party scripts probe the
    // parent frame and get blocked (FORMS-WEB-CX, QX, V1)
    /Blocked a frame with origin/i,
    // iOS WKWebView native bridge didn't answer postMessage (FORMS-WEB-N1)
    /WKWebView API client did not respond/i,
    // Crashes inside bundled third-party libraries (no first-party logic involved):
    // ProseMirror position mapping during mobile IME input in @smartsuite/smartdoc
    // (FORMS-WEB-WA) and react-scrollbars-custom reading e.touches[0] on a mouse event
    // (FORMS-WEB-WC)
    /Position \d+ out of range/i,
    /evaluating 'e\.touches\[0\]'/,
    // ProseMirror deleting a selection in a doc whose stored content does not satisfy the editor
    // schema (FORMS-WEB-XQ). Thrown from a keydown handler, so SmartdocFieldControl's
    // ErrorBoundary cannot catch it; sanitizing the incoming doc belongs to @smartsuite/smartdoc.
    /Called contentMatchAt on a node with invalid content/,
    // Injected third-party agents that patch built-ins, all from <anonymous> scripts outside the
    // bundle: an endpoint-monitoring agent hooking setTimeout (FORMS-WEB-E6, XT), the same agent
    // redefining Symbol.hasInstance (FORMS-WEB-XP), extension message ports (FORMS-WEB-XR) and the
    // Zalo in-app browser (FORMS-WEB-RR)
    /Right-hand side of 'instanceof' is not an object/,
    /can't redefine non-configurable property "?Symbol\.hasInstance"?/i,
    /Could not establish connection\. Receiving end does not exist/,
    /Can't find variable: zaloJSV2/,
    // A script outside the bundle patched HTMLElement.appendChild and JSON.stringify'd the appended
    // node; React's __reactFiber$* back-reference makes any DOM node circular. Matched on that
    // marker so a genuinely circular form value in our own JSON.stringify calls still reports
    // (FORMS-WEB-X3, X4). The footer that trips it is also isolated behind its own ErrorBoundary.
    /Converting circular structure to JSON[\s\S]*__reactFiber/,
    // iOS in-app browser (Chrome Mobile iOS / Google app) form-scanner script, injected as a
    // WKUserScript: its minified two-letter error names shift with every release (`La`,
    // `Ka\`prod`, `ga`, `Ba`, …) and a two-letter message never comes from our code
    // (FORMS-WEB-YN, YM, YJ). Stack-bearing siblings are dropped by hasOnlyForeignFrames.
    /^[A-Za-z]{2}(?:`[a-z]+)?$/,
    // Rabby Wallet extension content script rejecting with a plain EIP-1193 provider error object
    // (code 4900, "Message channel disconnected"); stackless, so denyUrls never sees it. Our own
    // async code rejects with Error instances (FORMS-WEB-YH, YG)
    "Object captured as promise rejection with keys: code, message",
  ],
  denyUrls: [
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    /^safari-web-extension:\/\//i,
    // Safari App Extensions (PayPal Honey, etc.) inject scripts from a file path,
    // not a safari-web-extension:// URL (FORMS-WEB-W4)
    /\.safariextension\//i,
    // Facebook/Instagram in-app browser injected autofill scripts (FORMS-WEB-VW)
    /^iabjs:\/\//i,
    // Google Maps internal script errors (FORMS-WEB-N0)
    /maps-api-v3\//i,
  ],
  beforeSend: (event) => (hasOnlyForeignFrames(event) ? null : event),
});

// Attach Session Replay after first paint: lazyLoadIntegration pulls it from
// the Sentry CDN, keeping ~100 kB of recording code out of the main bundle
// and off the form's critical path. Errors thrown before `load` are still
// captured (without a replay attached) — an accepted trade-off.
if (env.REACT_APP_SENTRY_ENVIRONMENT) {
  const attachReplay = (): void => {
    Sentry.lazyLoadIntegration("replayIntegration")
      .then((replayIntegration) => {
        Sentry.addIntegration(replayIntegration());
      })
      .catch(() => {
        // Replay is best-effort telemetry; a blocked CDN must not break the form.
      });
  };
  if (document.readyState === "complete") {
    attachReplay();
  } else {
    window.addEventListener("load", attachReplay, { once: true });
  }
}

// Initializes field logic for limiting visibility of fields and processing values
initFieldsLogicRegistry(
  {} as FieldTypesConfig,
  fieldTypes,
  new MockAppRecordsService({}, null as unknown as Member)
);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  // <React.StrictMode>
  <GlobalErrorBoundary>
    <App />
  </GlobalErrorBoundary>
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
